import {
    AfterViewInit,
    Component,
    ElementRef,
    EventEmitter,
    Input,
    OnChanges,
    Output,
    SimpleChanges,
    ViewChild,
} from '@angular/core';

import { MultiSelectDropdownConfig } from './interfaces/multi-select-dropdown-config.interface';
import { MultiSelectDropdownItem } from './interfaces/multi-select-dropdown-item.interface';

import { DropdownHelperService } from 'Angular/services/dropdown-helper.service';

/**
 * Multi-Select Dropdown Component
 *
 * This component shows a list of items and checkboxes in dropdown. The checkboxes show whether or not
 * The corresponding item is currently selected.
 *
 * Input Properties:
 *      buttonClass: string that is the CSS class to apply to the button that opens the dropdown
 *      config: MultiSelectDropdownConfig which has the dropdown title and list of items to be in the dropdown
 *      dropdownClass: string that is The CSS class to be applied to the dropdown menu itself
 *
 * Output Properties:
 *      changeHandler: EventEmitter that emits the item whenever an item is selected or deselected
 *      changeHandlerAll: EventEmitter that emits the list of all items whenever a change is detected
 *      closeHandler: EventEmitter that emits whenever the dropdown is closed
 *
 * Internal Properties:
 *      isOpen: boolean for whether or not the dropdown is display the selectable items
 *      countSelected: number that represents the count of items in the dropdown that are currently selected
 */
@Component({
    standalone: false,
    selector: 'ycn-multi-select-dropdown',
    templateUrl: './multi-select-dropdown.template.html',
})
export class MultiSelectDropdownComponent implements AfterViewInit, OnChanges {
    @Input() buttonClass!: string;

    @Input() config!: MultiSelectDropdownConfig;

    @Input() dropdownClass!: string;

    @Input() dropdownContainerClass!: string;

    @Input() dropdownItemClass!: string;

    @Input() inputDisabled!: boolean;

    @Output() changeHandler = new EventEmitter<MultiSelectDropdownItem>();

    @Output() changeHandlerAll = new EventEmitter<MultiSelectDropdownItem[]>();

    @Output() closeHandler = new EventEmitter<void>();

    @ViewChild('multiSelectDropdown') el!: ElementRef;

    configCopy!: MultiSelectDropdownConfig;

    isOpen: boolean = false;

    countSelected: number = 0;

    constructor(private dropdownHelperService: DropdownHelperService) {}

    ngOnInit(): void {
        this.setSelectAllStatus(this.allItemsAreSelected());
    }

    ngAfterViewInit(): void {
        const autoCloseHandler = (): void => {
            if (this.isOpen) {
                this.toggle();
            }
        };
        this.dropdownHelperService.setCloseHandler(this.el.nativeElement, autoCloseHandler);
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.config?.currentValue) {
            this.configCopy = { ...changes.config.currentValue };
        }
        this.countSelected = this.configCopy.list.filter((item) => item.selected).length;
    }

    toggle(): void {
        if (!this.inputDisabled) {
            if (this.isOpen) {
                this.isOpen = false;
                this.closeHandler.emit();
            } else {
                this.isOpen = true;
            }
        } else {
            this.isOpen = false;
        }
    }

    allItemsAreSelected(): boolean {
        const allSelectableItems = this.config.list.filter((item: MultiSelectDropdownItem) => !item.selectAll);
        const selectedSelectableItems = this.config.list.filter(
            (item: MultiSelectDropdownItem) => item.selected && !item.selectAll
        );
        return allSelectableItems.length === selectedSelectableItems.length;
    }

    setAllItemsSelectedStatus(status: boolean): void {
        this.config.list.forEach((item: MultiSelectDropdownItem) => {
            item.selected = !!status; // eslint-disable-line no-param-reassign
        });
    }

    setSelectAllStatus(status: boolean): void {
        this.config.list.forEach((item: MultiSelectDropdownItem) => {
            if (item.selectAll) item.selected = !!status; // eslint-disable-line no-param-reassign
        });
    }

    handleChange(item: MultiSelectDropdownItem): void {
        if (item.selectAll) {
            if (item.selected) {
                // select all items
                this.setAllItemsSelectedStatus(true);
                this.countSelected = this.configCopy.list.length;
            } else {
                // de-select all items
                this.setAllItemsSelectedStatus(false);
                this.countSelected = 0;
            }
        } else {
            this.countSelected = item.selected ? this.countSelected + 1 : this.countSelected - 1;
            if (item.selected && this.allItemsAreSelected()) {
                this.setSelectAllStatus(true);
            }
            if (!item.selected) {
                this.setSelectAllStatus(false);
            }
        }

        this.changeHandler.emit(item);
        this.changeHandlerAll.emit(this.config.list);
    }
}
